import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import Dashboard from './Dashboard/Dashboard';
import MainLayout from './Layouts/MainLayout';
import { AuthProvider, useAuth } from './stateManagers/services/AuthContext';
import { LoadingScreen } from './components/LoadingScreen';
import LoginScreen from './Layouts/Authorisation/LoginScreen';
import InstituteProfile from './Layouts/MyProfile/InstituteProfile';
import Branches from './Layouts/Branches/Branches';
import BranchDetails from './Layouts/Branches/BranchDetails';
import { getPathForRoute } from './config/routeConfig';
import AddStudent from './Layouts/Students/AddStudent';
import Students from './Layouts/Students/Students';
import Batches from './Layouts/Batches/Batches';
import NoticeBoard from './Layouts/NoticeBoard/NoticeBoard';
import Teachers from './Layouts/Teachers/Teachers';
import AddTeacher from './Layouts/Teachers/AddTeacher';
import StudyMaterials from './Layouts/Studymaterials/StudyMaterials';
import StudentDetails from './Layouts/Students/StudentDetails';
import EditStudent from './Layouts/Students/EditStudent';
import TeacherDetails from './Layouts/Teachers/Teacherdetails';
import EditTeacher from './Layouts/Teachers/EditTeacher';
import StudentsFees from './Layouts/Fees/StudentsFees';
import TeacherPermissionsSettings from './Layouts/Settings/TeacherPermissionsSettings';
import BatchDetails from './Layouts/Batches/BatchDetails';
import IdCardGenerator from './Layouts/IdCardGenerator/IdCardGenerator';

const AppContent: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [contextData, setContextData] = useState<Record<string, any>>({});

  // Universal navigation handler - navigate by route ID
  const handleNavigation = (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => {
    if (data) {
      setContextData(prev => ({ ...prev, ...data }));
    }
    const path = getPathForRoute(routeId, params);
    navigate(path);
  };

  // Navigate directly to a path (for dynamic routes)
  const handleNavigateTo = (path: string, data?: Record<string, any>) => {
    if (data) {
      setContextData(prev => ({ ...prev, ...data }));
    }
    navigate(path);
  };

  // Clear context and go back
  const handleNavigateBack = () => {
    setContextData({});
    navigate(-1);
  };

  // Logout handlers
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutModal(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  return (
    <>
      <MainLayout
        user={user}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
        onLogout={handleLogoutClick}
        contextData={contextData}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/institute-profile" element={<InstituteProfile />} />
          <Route 
            path="/branches" 
            element={<Branches onBranchClick={(id) => handleNavigateTo(`/branches/${id}`)} />} 
          />
          <Route 
            path="/branches/:branchId" 
            element={
              <BranchDetailsWrapper 
                onBack={handleNavigateBack}
                onBranchLoad={(name) => setContextData(prev => ({ ...prev, branchName: name }))}
              />
            } 
          />
          <Route path="/students">
            <Route index element={<Students />} />
            <Route path="add-student" element={<AddStudent />} />
            <Route path=":studentId" element={<StudentDetails />} />
            <Route path="/students/edit/:studentId" element={<EditStudent />} />
          </Route>
          

          {/* Teacher Routes */}
          <Route path="/teachers">
            <Route index element={<Teachers />} />
            <Route path="add-teacher" element={<AddTeacher />} />
            <Route path=":teacherId" element={<TeacherDetails />} />  {/* NEW */}
            <Route path="edit/:teacherId" element={<EditTeacher />} /> {/* NEW */}
          </Route>

          <Route path="batches" element={<Batches />} />
          <Route path="batches/:batchId/details" element={<BatchDetails />} /> {/* ← ADD THIS */}
          
            <Route path="study-materials" element={<StudyMaterials />} />
            <Route path="noticeboard" element={<NoticeBoard />} />
            <Route path="id-card-generator" element={<IdCardGenerator />} />
            <Route path="students-fees" element={<StudentsFees />} />

          
          <Route path="/settings/teacher-permissions" element={<TeacherPermissionsSettings />} />
          <Route path="/school-setup/database" element={<div style={{ padding: '40px' }}><h2>Database Setup Component</h2></div>} />
          <Route path="*" element={
            <div style={{ padding: '40px' }}>
              <h2 style={{ 
                fontSize: '1.8rem', 
                fontWeight: '600', 
                color: '#1a1a1a',
                marginBottom: '16px'
              }}>
                Welcome, {user?.email}!
              </h2>
              <p style={{ color: '#666', fontSize: '1rem' }}>
                Select a menu item from the sidebar to get started.
              </p>
            </div>
          } />
        </Routes>
      </MainLayout>

      {showLogoutModal && (
        <LogoutModal onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
    </>
  );
};

// Logout confirmation modal component
const LogoutModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  }}>
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '32px',
      maxWidth: '400px',
      width: '100%',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        background: '#fff3cd',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        <i className="pi pi-exclamation-triangle" style={{ 
          fontSize: '1.5rem', 
          color: '#856404' 
        }}></i>
      </div>

      <h3 style={{
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#1a1a1a',
        margin: '0 0 8px 0'
      }}>
        Confirm Logout
      </h3>
      
      <p style={{
        fontSize: '1rem',
        color: '#666',
        margin: '0 0 24px 0',
        lineHeight: '1.5'
      }}>
        Are you sure you want to logout? You'll need to login again to access your dashboard.
      </p>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '14px 24px',
            background: '#f5f5f5',
            color: '#333',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#e0e0e0'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#f5f5f5'}
        >
          Cancel
        </button>
        
        <button
          onClick={onConfirm}
          style={{
            flex: 1,
            padding: '14px 24px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#c82333'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
        >
          Logout
        </button>
      </div>
    </div>
  </div>
);

// Wrapper component to handle route params
const BranchDetailsWrapper: React.FC<{
  onBack: () => void;
  onBranchLoad: (name: string) => void;
}> = ({ onBack, onBranchLoad }) => {
  const { branchId } = useParams<{ branchId: string }>();
  if (!branchId) return null;
  return <BranchDetails branchId={branchId} onBack={onBack} onBranchLoad={onBranchLoad} />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;